import React from 'react';
import { motion } from 'framer-motion';
import {
  CalendarIcon,
  TrendingUpIcon,
  TrendingDownIcon,
  ZapIcon,
  WalletIcon,
  GaugeIcon } from
'lucide-react';
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useTelemetryHistory } from '../../hooks/useHistoricalTelemetry';
import { whToKwh } from '../../utils/energyUtils';
import { useTranslation } from 'react-i18next';

const today = new Date();
const currentDay = today.getDate();
const daysInMonth = new Date(
  today.getFullYear(),
  today.getMonth() + 1,
  0
).getDate();

// Korean progressive electricity tiers (monthly, household)
const TIERS = [
{
  label: '1단계',
  max: 200,
  rate: 120,
  color: '#22d3ee',
  bgColor: 'rgba(34,211,238,0.15)'
},
{
  label: '2단계',
  max: 400,
  rate: 214.6,
  color: '#f59e0b',
  bgColor: 'rgba(245,158,11,0.15)'
},
{
  label: '3단계',
  max: 600,
  rate: 307.3,
  color: '#ef4444',
  bgColor: 'rgba(239,68,68,0.15)'
}];

function getTier(kwh: number) {
  if (kwh <= 200) return 1;
  if (kwh <= 400) return 2;
  return 3;
}
function calcBill(kwh: number) {
  let bill = 0;
  let remaining = kwh;
  // Tier 1: 0~200
  const t1 = Math.min(remaining, 200);
  bill += t1 * 120;
  remaining -= t1;
  // Tier 2: 201~400
  if (remaining > 0) {
    const t2 = Math.min(remaining, 200);
    bill += t2 * 214.6;
    remaining -= t2;
  }
  // Tier 3: 401+
  if (remaining > 0) {
    bill += remaining * 307.3;
  }
  // Base fee by tier
  const tier = getTier(kwh);
  if (tier === 1) bill += 910;else
  if (tier === 2) bill += 1600;else
  bill += 7300;
  return Math.round(bill);
}
// SVG Gauge component for progressive tier
function TierGauge({
  currentKwh,
  maxKwh = 600
}: {currentKwh: number;maxKwh?: number;}) {
  const tier = getTier(currentKwh);
  const tierInfo = TIERS[tier - 1];
  // Gauge geometry
  const cx = 90;
  const cy = 78;
  const r = 62;
  
  // Arc path helper
  const arcPath = (startDeg: number, endDeg: number, radius: number) => {
    // For SVG, we go clockwise (decreasing angle from 180 to 0)
    const rad = Math.PI / 180;
    const sx = cx + radius * Math.cos(startDeg * rad);
    const sy = cy - radius * Math.sin(startDeg * rad);
    const ex = cx + radius * Math.cos(endDeg * rad);
    const ey = cy - radius * Math.sin(endDeg * rad);
    const largeArc = Math.abs(startDeg - endDeg) > 180 ? 1 : 0;
    return `M ${sx} ${sy} A ${radius} ${radius} 0 ${largeArc} 1 ${ex} ${ey}`;
  };

  // Tier boundaries as angles (180° = 0kWh, 0° = maxKwh)
  const kwhToAngle = (kwh: number) =>
  180 - Math.min(kwh, maxKwh) / maxKwh * 180;
  const tier1End = kwhToAngle(200);
  const tier2End = kwhToAngle(400);

  // Needle angle
  const needleAngle = kwhToAngle(Math.min(currentKwh, maxKwh));
  const needleRad = needleAngle * Math.PI / 180;
  const needleLen = r - 10;
  const needleX = cx + needleLen * Math.cos(needleRad);
  const needleY = cy - needleLen * Math.sin(needleRad);
  const strokeWidth = 10;

  return (
    <div className="flex flex-col items-center">
      <svg
        viewBox="0 0 180 95"
        className="w-full"
        style={{
          maxWidth: '8rem'
        }}>
        
        {/* Background track */}
        <path
          d={arcPath(180, 0, r)}
          fill="none"
          stroke="rgba(255,255,255,0.05)"
          strokeWidth={strokeWidth + 2}
          strokeLinecap="round" />
        
        {/* Tier 1: cyan */}
        <path
          d={arcPath(180, tier1End, r)}
          fill="none"
          stroke="#22d3ee"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity={0.6} />
        
        {/* Tier 2: amber */}
        <path
          d={arcPath(tier1End, tier2End, r)}
          fill="none"
          stroke="#f59e0b"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity={0.6} />
        
        {/* Tier 3: red */}
        <path
          d={arcPath(tier2End, 0, r)}
          fill="none"
          stroke="#ef4444"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          opacity={0.6} />
        

        {/* Tier boundary ticks */}
        {[200, 400].map((kwh) => {
          const a = kwhToAngle(kwh);
          const rad = a * Math.PI / 180;
          const innerR = r - strokeWidth / 2 - 2;
          const outerR = r + strokeWidth / 2 + 2;
          return (
            <line
              key={kwh}
              x1={cx + innerR * Math.cos(rad)}
              y1={cy - innerR * Math.sin(rad)}
              x2={cx + outerR * Math.cos(rad)}
              y2={cy - outerR * Math.sin(rad)}
              stroke="rgba(255,255,255,0.2)"
              strokeWidth={1.5} />);


        })}

        {/* Needle */}
        <motion.line
          initial={{
            x2: cx + needleLen * Math.cos(Math.PI),
            y2: cy - needleLen * Math.sin(Math.PI)
          }}
          animate={{
            x2: needleX,
            y2: needleY
          }}
          transition={{
            type: 'spring',
            stiffness: 60,
            damping: 15,
            delay: 0.3
          }}
          x1={cx}
          y1={cy}
          stroke="var(--mobile-text-primary)"
          strokeWidth={2}
          strokeLinecap="round" />
        
        {/* Needle center dot */}
        <circle cx={cx} cy={cy} r={4} fill={tierInfo.color} />
        <circle cx={cx} cy={cy} r={2} fill="var(--mobile-bg-card)" />

        {/* Tier labels */}
        <text x="18" y="88" fill="#475569" fontSize="8" fontFamily="monospace">0</text>
        {(() => {
          const a200 = kwhToAngle(200);
          const rad200 = a200 * Math.PI / 180;
          const lx = cx + (r + 14) * Math.cos(rad200);
          const ly = cy - (r + 14) * Math.sin(rad200);
          return (
            <text key="200" x={lx} y={ly} fill="#475569" fontSize="8" fontFamily="monospace" textAnchor="middle">200</text>
          );
        })()}
        {(() => {
          const a400 = kwhToAngle(400);
          const rad400 = a400 * Math.PI / 180;
          const lx = cx + (r + 14) * Math.cos(rad400);
          const ly = cy - (r + 14) * Math.sin(rad400);
          return (
            <text key="400" x={lx} y={ly} fill="#475569" fontSize="8" fontFamily="monospace" textAnchor="middle">400</text>
          );
        })()}
        <text x="155" y="88" fill="#475569" fontSize="8" fontFamily="monospace">600</text>
      </svg>

      {/* Current tier badge */}
      <div
        className="flex items-center gap-1 px-2 py-0.5 rounded-full -mt-0.5"
        style={{
          backgroundColor: tierInfo.bgColor
        }}>
        
        <div
          className="w-1.5 h-1.5 rounded-full"
          style={{
            backgroundColor: tierInfo.color
          }} />
        
        <span
          className="text-[0.6rem] font-bold font-mono"
          style={{
            color: tierInfo.color
          }}>
          
          누진 {tierInfo.label}
        </span>
      </div>
    </div>);

}

export function MonthlyEnergyWidget({ currentAssetId }: { currentAssetId: string | null }) {
  const { t } = useTranslation();
  const { data: telemetryData, loading } = useTelemetryHistory(
    currentAssetId || '',
    ['daily_power'],
    'monthly',
    'ASSET'
  );

  // Format chart data
  const chartData = React.useMemo(() => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();
    const todayNum = currentDate.getDate();
    
    const lastMonthDate = new Date(currentDate);
    lastMonthDate.setMonth(currentMonth - 1);
    const lastMonth = lastMonthDate.getMonth();
    const lastYear = lastMonthDate.getFullYear();

    const daysInMonthCount = new Date(currentYear, currentMonth + 1, 0).getDate();
    
    const currentMonthMap = new Map<number, number>();
    const lastMonthMap = new Map<number, number>();

    if (telemetryData && telemetryData['daily_power']) {
      telemetryData['daily_power'].forEach((item: { ts: number; value: string }) => {
        const d = new Date(item.ts);
        const m = d.getMonth();
        const y = d.getFullYear();
        const date = d.getDate();
        const val = whToKwh(item.value);
        
        if (m === currentMonth && y === currentYear) {
          currentMonthMap.set(date, val);
        } else if (m === lastMonth && y === lastYear) {
          lastMonthMap.set(date, val);
        }
      });
    }

    const newData = [];
    for (let i = 1; i <= daysInMonthCount; i++) {
        newData.push({
            day: i.toString(),
            lastMonthKwh: lastMonthMap.get(i) || 0,
            currentKwh: i <= todayNum ? (currentMonthMap.get(i) || 0) : null
        });
    }
    return newData;
  }, [telemetryData]);

  // Calculate usage totals from chartData to avoid processing telemetryData twice
  const { currentUsage, lastMonthUsage } = React.useMemo(() => {
     let cur = 0;
     let last = 0;
     const todayNum = new Date().getDate();

     chartData.forEach(item => {
         if (item.currentKwh !== null) {
             cur += item.currentKwh;
         }
         if (parseInt(item.day, 10) <= todayNum) {
             last += item.lastMonthKwh;
         }
     });
     
     return { 
       currentUsage: cur, 
       lastMonthUsage: last
     };
  }, [chartData]);

  const percentChange = lastMonthUsage > 0 
    ? ((currentUsage - lastMonthUsage) / lastMonthUsage * 100).toFixed(1)
    : '0.0';
    
  const isDown = currentUsage < lastMonthUsage;
  
  // Projections
  const dailyAvg = currentDay > 0 ? (currentUsage / currentDay) : 0;
  const projectedUsage = Math.round(dailyAvg * daysInMonth);
  const projectedTier = getTier(projectedUsage);
  const projectedBill = calcBill(projectedUsage);
  const projectedTierInfo = TIERS[projectedTier - 1];

  return (
    <div className="bg-[var(--mobile-bg-card)] border border-[var(--mobile-border)] shadow-sm rounded-2xl p-3 flex flex-col gap-2 flex-1 h-full overflow-hidden">
      <div className="flex items-center space-x-2 text-[var(--mobile-text-muted)]">
        <CalendarIcon className="w-4 h-4 text-cyan-400" />
        <span className="text-[0.95rem] text-[var(--mobile-text-primary)] font-medium uppercase tracking-wider">
          {t('monthly_energy.title')}
        </span>
      </div>

      {/* Row 1: Cumulative + Gauge */}
      <div className="flex gap-2">
        {/* Left: Cumulative usage */}
        <div className="flex-1 bg-[var(--mobile-bg-sub)] rounded-xl p-2 border border-[var(--mobile-border-strong)] flex flex-col justify-center">
          <div className="text-[0.7rem] text-[var(--mobile-text-muted)] uppercase tracking-wider font-bold mb-0.5">
            {t('monthly_energy.cumulative_usage')}
          </div>
          <div className="flex items-baseline gap-1">
             <span className="text-2xl font-mono font-bold text-[var(--mobile-text-primary)]">
              {currentUsage.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
             </span>
             <span className="text-[0.75rem] text-[var(--mobile-text-muted)] font-mono">kWh</span>
          </div>
          <div
            className={`flex items-center gap-1 mt-1 text-[0.65rem] font-mono ${Number(percentChange) === 0 ? 'text-slate-400' : isDown ? 'text-emerald-400' : 'text-red-400'}`}>
            
            {Number(percentChange) !== 0 && (
              isDown ? <TrendingDownIcon className="w-3 h-3" /> : <TrendingUpIcon className="w-3 h-3" />
            )}
            
            <span>
              {Number(percentChange) === 0 
                ? t('monthly_energy.vs_last_month_same')
                : isDown 
                  ? t('monthly_energy.vs_last_month_decreased', { val: Math.abs(Number(percentChange)) })
                  : t('monthly_energy.vs_last_month_increased', { val: Math.abs(Number(percentChange)) })}
            </span>
          </div>
        </div>

        {/* Right: Tier Gauge */}
        <div className="flex-1 bg-[var(--mobile-bg-sub)] rounded-xl p-1 border border-[var(--mobile-border-strong)] flex items-center justify-center relative">
          {loading && <div className="absolute inset-0 flex items-center justify-center bg-[var(--mobile-bg-sub)]/50 rounded-xl z-10"><span className="text-[0.6rem] text-cyan-400 animate-pulse">Loading...</span></div>}
          <TierGauge currentKwh={currentUsage} />
        </div>
      </div>

      {/* Row 2: Projected stats */}
      <div className="grid grid-cols-3 gap-2">
        {/* Projected usage */}
        <div className="bg-[var(--mobile-bg-sub)] rounded-xl p-2 border border-[var(--mobile-border-strong)]">
          <div className="text-[0.7rem] text-[var(--mobile-text-muted)] uppercase tracking-wider font-bold mb-0.5">
            {t('monthly_energy.projected_usage')}
          </div>
          <div className="flex items-baseline gap-0.5">
            <span className="text-base font-mono font-bold text-cyan-400">
              {projectedUsage.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
            </span>
            <span className="text-[0.7rem] text-[var(--mobile-text-muted)] font-mono">kWh</span>
          </div>
        </div>

        {/* Projected tier */}
        <div className="bg-[var(--mobile-bg-sub)] rounded-xl p-2 border border-[var(--mobile-border-strong)]">
          <div className="text-[0.7rem] text-[var(--mobile-text-muted)] uppercase tracking-wider font-bold mb-0.5">
            {t('monthly_energy.projected_tier')}
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="w-2 h-2 rounded-full flex-shrink-0"
              style={{
                backgroundColor: projectedTierInfo.color
              }} />
            
            <span
              className="text-base font-mono font-bold"
              style={{
                color: projectedTierInfo.color
              }}>
              {t('monthly_energy.tier_level', { stage: projectedTier })}
            </span>
          </div>
        </div>

        {/* Projected bill */}
        <div className="bg-[var(--mobile-bg-sub)] rounded-xl p-2 border border-[var(--mobile-border-strong)]">
          <div className="text-[0.7rem] text-[var(--mobile-text-muted)] uppercase tracking-wider font-bold mb-0.5">
            {t('monthly_energy.projected_bill')}
          </div>
          <div className="flex items-baseline gap-0.5">
            <span className="text-base font-mono font-bold text-amber-400">
              {projectedBill.toLocaleString()}
            </span>
            <span className="text-[0.8rem] text-[var(--mobile-text-muted)] font-mono">{t('monthly_energy.currency')}</span>
          </div>
        </div>
      </div>

      {/* Row 3: 일별 사용량 차트 */}
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-1.5 flex-shrink-0">
          <span className="text-[0.8rem] text-slate-500 uppercase tracking-wider font-bold">
            {t('monthly_energy.daily_usage')}
          </span>
          <span className="text-[0.7rem] text-slate-600 font-mono">
            {t('monthly_energy.date_range', { currentDay })}
          </span>
        </div>
        <div
          className="h-[120px]"
          style={{
            minHeight: '2.5rem'
          }}>
          
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barCategoryGap="15%" barGap="-100%">
              <XAxis
                dataKey="day"
                axisLine={false}
                tickLine={false}
                tick={{
                  fill: '#475569',
                  fontSize: '0.6rem',
                  fontFamily: 'monospace'

                }}
                interval={1} />
                
              <Tooltip
                cursor={{
                  fill: 'var(--mobile-bg-sub)'
                }}
                contentStyle={{
                  backgroundColor: 'var(--mobile-bg-card)',
                  borderColor: 'var(--mobile-border-strong)',
                  borderRadius: '8px',
                  fontSize: '10px',
                  fontFamily: 'monospace',
                  color: 'var(--mobile-text-primary)'
                }}
                itemStyle={{
                  color: '#22d3ee'
                }}
                labelFormatter={(label) => t('monthly_energy.day_label', { label })}
                formatter={(value: any, name: string) => {
                  if (value === null || value === undefined) return [];
                  const valNum = Number(value).toFixed(1) + ' kWh';
                  if (name === 'lastMonthKwh') return [valNum, t('monthly_energy.last_month_usage') || '지난달'];
                  if (name === 'currentKwh') return [valNum, t('monthly_energy.current_usage') || '이번달'];
                  return [valNum, name];
                }} />
              
              <Bar
                dataKey="lastMonthKwh"
                radius={[2, 2, 0, 0]}
                fill="#475569"
                opacity={0.3}
                minPointSize={0} />
              
              <Bar
                dataKey="currentKwh"
                radius={[2, 2, 0, 0]}
                fill="#22d3ee"
                opacity={0.8}
                minPointSize={2} />
              
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>);

}